'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocale } from 'next-intl';

//* Context
import { withLanguageContext, withUIContext } from '@/context';

//* Components
import Text from '@/components/Text';
import Editor from '@/components/Editor';
import Container from '@/components/Container';
import useTrigger from '@/components/Animation/useTrigger';
import TextAnimation from '@/components/Animation/TextAnimation/TextAnimation';

//* Styles
import PrivacyPolicyTemplateStyle from './style';

//* Data
import { dataPrivacyPolicy } from '@/data';

const PrivacyPolicyTemplate = ({ translate, winWidth, winHeight }) => {
	const lang = useLocale();

	//! State
	const [activeIndex, setActiveIndex] = useState(null);

	//! Refs
	const descriptionItems = useRef([]);
	const titleRef = useRef();

	//! Checks
	const isMobile = useMemo(() => {
		return winWidth < 768;
	}, [winWidth]);

	//! Handle list link click
	const handleListLinkClick = useCallback(
		(clickedIndex) => {
			const element = document?.querySelector('.is-distance');
			const paddingTopWithPX = element ? getComputedStyle(element)['paddingTop'].trim() : 120;
			const paddingTopWithValue = parseInt(paddingTopWithPX, 10);

			window.scrollTo({
				top: window.pageYOffset + descriptionItems.current?.[clickedIndex].getBoundingClientRect().top - paddingTopWithValue,
				behavior: 'smooth',
			});
		},
		[descriptionItems]
	);

	//! Store list
	const storeLinks = useMemo(() => {
		return dataPrivacyPolicy[lang]['titles'].map(({ title }, index) => (
			<Text
				key={index}
				onClick={() => handleListLinkClick(index)}
				className={`p p3 text-primary-color cursor-pointer sticky-item transition ${index === activeIndex ? 'active' : ''}`}>
				{title}
			</Text>
		));
	}, [dataPrivacyPolicy, lang, activeIndex]);

	//! Store content
	const storeContent = useMemo(() => {
		return dataPrivacyPolicy[lang]['contents'].map((item, index) => {
			return (
				<div
					key={index}
					className={`content-block`}
					ref={(ref) => (descriptionItems.current[index] = ref)}>
					<Text className={`description-title uppercase font-montserrat-arm-medium h6`}>{item.title}</Text>

					<Editor data={item.description} />
				</div>
			);
		});
	}, [dataPrivacyPolicy, lang]);

	//! Scroll
	const scroll = () => {
		let arr = [];

		for (let i = 0; i < descriptionItems.current.length; i++) {
			if (descriptionItems.current[i].getBoundingClientRect().top < winHeight * 0.3) {
				arr.push(i);
			}
		}

		if (arr.length === 0) {
			setActiveIndex(0);
		} else {
			setActiveIndex(arr.length - 1);
		}
	};

	//! Scroll effect
	useEffect(() => {
		window.addEventListener('scroll', scroll);

		const trigger = useTrigger({
			ref: titleRef.current,
			documentPercent: 90,
			onEnter: () => {
				titleRef.current.children[0].classList.add('active');
			},
		});

		return () => {
			window.removeEventListener('scroll', scroll);
			trigger();
		};
	}, []);

	return (
		<PrivacyPolicyTemplateStyle>
			<Container>
				<div className={`pp-top-block`}>
					<Text
						ref={titleRef}
						tag={'h1'}
						className={'text-primary-color font-montserrat-arm-medium uppercase'}
						text={'privacyPolicy'}>
						<TextAnimation
							delay={0.2}
							text={'privacyPolicy'}
						/>
					</Text>

					<div className={`date-block`}>
						<div className='date-inner-block'>
							<Text className={'p p3 text-secondary-color date-item'}>
								{translate('effectiveDate')}: <span className={`underline`}>{dataPrivacyPolicy['effectiveDate']}</span>
							</Text>

							<Text className={'p p3 text-secondary-color date-item'}>
								{translate('lastUpdated')}: <span className={`underline`}>{dataPrivacyPolicy['lastUpdated']}</span>
							</Text>
						</div>
					</div>
				</div>

				<div className='pp-bottom-block'>
					<Container
						row
						full>
						{isMobile ? null : (
							<div className='col-4 col-m-12 sticky-panel'>
								<div className='sticky-content'>
									<div className='sticky-inner-content'>{storeLinks}</div>
								</div>
							</div>
						)}

						<div className='col-8 col-m-12 right-panel'>{storeContent}</div>
					</Container>
				</div>
			</Container>
		</PrivacyPolicyTemplateStyle>
	);
};

export default withLanguageContext(withUIContext(PrivacyPolicyTemplate, ['winWidth', 'winHeight']), ['translate']);
